interface GenerateZonedStartTimeProps {
  subtractSeconds?: number;
  subtractDays?: number;
  subtractHours?: number;
  subtractMinutes?: number;
  subtractWeeks?: number;
  subtractMonths?: number;
  startOf?: string;
}

const PAD = (n: number, width = 2): string => String(n).padStart(width, '0');
const unitName = (unit: string): string | undefined => {
  if (typeof unit !== 'string') return undefined;
  const name = unit.toLowerCase().replace(/s$/, '');
  return ['second', 'minute', 'hour', 'day', 'week', 'month', 'year'].includes(name) ? name : undefined;
};

const fixedOffset = (tz: string): number | undefined => {
  if (['Z', 'UTC', 'UTCz'].includes(tz)) return 0;
  const match = /^([+-])(\d{2}):(\d{2})$/.exec(tz);
  if (!match || Number(match[2]) > 23 || Number(match[3]) > 59) return undefined;
  return (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]));
};

/** ISO date text without a suffix is UTC. Reject rollover and invalid input. */
export const parseTimestamp = (stamp: unknown): Date | undefined => {
  if (stamp instanceof Date || typeof stamp === 'number') {
    const date = new Date(stamp instanceof Date ? stamp.getTime() : stamp);
    return Number.isFinite(date.getTime()) ? date : undefined;
  }
  if (typeof stamp !== 'string') return undefined;
  const match = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:\d{2})?)?$/.exec(stamp);
  if (!match) return undefined;
  const wall = `${match[1]}T${match[2] ?? '00'}:${match[3] ?? '00'}:${match[4] ?? '00'}.${(match[5] ?? '').padEnd(3, '0')}`;
  const plain = new Date(`${wall}Z`);
  if (!Number.isFinite(plain.getTime()) || plain.toISOString() !== `${wall}Z`) return undefined;
  const tz = match[6] ?? 'Z';
  if (fixedOffset(tz) === undefined) return undefined;
  const date = new Date(`${wall}${tz}`);
  return Number.isFinite(date.getTime()) ? date : undefined;
};

export const getZoneOffset = (
  timestamp: string | number | Date, timeZone = 'UTC',
): number | undefined => {
  const date = parseTimestamp(timestamp);
  if (!date || typeof timeZone !== 'string' || !timeZone) return undefined;
  const fixed = fixedOffset(timeZone);
  if (fixed !== undefined) return fixed;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone === 'local' ? undefined : timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
    const wall = parseTimestamp(`${values.year.padStart(4, '0')}-${values.month}-${values.day}T${values.hour}:${values.minute}:${values.second}Z`);
    if (!wall) return undefined;
    // Intl parts omit milliseconds: compare at the same precision.
    const offset = (wall.getTime() - Math.floor(date.getTime() / 1000) * 1000) / 60000;
    return Number.isInteger(offset) ? offset : undefined;
  } catch {
    return undefined;
  }
};

const offsetText = (offset: number): string => {
  const minutes = Math.abs(offset);
  return `${offset < 0 ? '-' : '+'}${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
};

export const getZonedMidnight = (
  stamp: string | number | Date, timeZone = 'UTC',
): Date | undefined => {
  const date = parseTimestamp(stamp);
  if (!date) return undefined;
  const day_text = `${date.toISOString().slice(0, 10)}T00:00:00`;
  const first_offset = getZoneOffset(`${day_text}Z`, timeZone);
  if (first_offset === undefined) return undefined;
  const first_guess = parseTimestamp(`${day_text}${offsetText(first_offset)}`);
  const offset = first_guess && getZoneOffset(first_guess, timeZone);
  return offset === undefined || offset === null ? first_guess : parseTimestamp(`${day_text}${offsetText(offset)}`);
};

export const FORMATTERS: Record<string, string> = {
  'default': `yyyy-MM-dd'T'HH:mm:ssXXX`,
  'chart-axis': 'M/dd',
  'fullDay': `EEEE, d MMM, p`,
  'full': `do MMM y, p`,
  'all': `do MMM y, h:mm:ss a`,
  'dayTime': `do MMM, p`,
  'date': 'd MMM y',
  'dateSm': 'd MMM',
  'axis': 'do MMM',
  'weekDay': `EEEE, d MMM y`,
  'WEEKsm': `w`,
  'WEEK': `d MMM y`,
  'MONTH': `MMM y`,
  'MONTHsm': 'd MMM',
  'DAY': `EEEE, d MMM y`,
  'DAYsm': `d MMM`,
  'AXISday': `do MMM`,
  'timeSm': `d MMM - h:mm a`,
  'timeLm': `h:mm:ss a`,
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_MS = 86400000;
const FORMAT_TOKENS = /'(?:[^']|'')*'|yyyy|y|MMM|MM|M|do|dd|d|EEEE|HH|hh|h|mm|ss|a|p|XXX|w/g;

const toOrdinal = (n: number): string => {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
  return `${n}${suffix}`;
};

const toOffsetText = (offset: number): string => {
  if (offset === 0) return 'Z';
  const absolute = Math.abs(offset);
  return `${offset < 0 ? '-' : '+'}${PAD(Math.floor(absolute / 60))}:${PAD(absolute % 60)}`;
};

const toLocalWeek = (year: number, month: number, day: number): number => {
  const weekStart = (time: number) => time - new Date(time).getUTCDay() * DAY_MS;
  const time = Date.UTC(year, month, day);
  if (time >= weekStart(Date.UTC(year + 1, 0, 1))) return 1;
  return Math.floor((time - weekStart(Date.UTC(year, 0, 1))) / (7 * DAY_MS)) + 1;
};

const formatWallTime = (time: number, offset: number, pattern: string): string => {
  const wall = new Date(time + offset * 60000);
  const year = wall.getUTCFullYear();
  const month = wall.getUTCMonth();
  const day = wall.getUTCDate();
  const hours = wall.getUTCHours();
  const hours_12 = hours % 12 || 12;
  const meridiem = hours < 12 ? 'AM' : 'PM';
  const tokens: Record<string, string> = {
    yyyy: PAD(year, 4), y: String(year),
    MMM: MONTH_NAMES[month], MM: PAD(month + 1), M: String(month + 1),
    do: toOrdinal(day), dd: PAD(day), d: String(day),
    EEEE: WEEKDAY_NAMES[wall.getUTCDay()],
    HH: PAD(hours), hh: PAD(hours_12), h: String(hours_12),
    mm: PAD(wall.getUTCMinutes()), ss: PAD(wall.getUTCSeconds()),
    a: meridiem, p: `${hours_12}:${PAD(wall.getUTCMinutes())} ${meridiem}`,
    XXX: toOffsetText(offset), w: String(toLocalWeek(year, month, day)),
  };
  return pattern.replace(FORMAT_TOKENS, (token) => token.startsWith("'")
    ? (token === "''" ? "'" : token.slice(1, -1).replace(/''/g, "'"))
    : tokens[token]);
};

const toLocalFieldsUtc = (date: Date): number => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(),
  date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());

const fromLocalWallTime = (date: Date, timeZone: string): string | undefined => {
  const wall = toLocalFieldsUtc(date);
  const first = getZoneOffset(toLocalFieldsUtc(new Date(wall)), timeZone);
  if (first === undefined) return undefined;
  let guess = wall - first * 60000;
  const second = getZoneOffset(guess, timeZone);
  if (second === undefined) return undefined;
  let offset = first;
  if (second !== first) {
    guess -= (second - first) * 60000;
    const third = getZoneOffset(guess, timeZone);
    offset = third === undefined || second === third ? second : Math.max(second, third);
  }
  return new Date(wall - offset * 60000).toISOString();
};

export const convertToZonedTimestamp = (
  timestamp: string | Date | number,
  timeZone?: string,
  format?: string | keyof typeof FORMATTERS,
): string | undefined => {
  let time_zone = timeZone || 'UTC';
  let stamp = timestamp || new Date();
  let utcz = false;
  const is_local = time_zone === 'local';
  if (time_zone === 'UTCz' || is_local) {
    time_zone = 'UTC';
    utcz = true;
  }
  if (stamp instanceof Date) {
    const utc_stamp = is_local ? stamp.toISOString() : fromLocalWallTime(stamp, time_zone);
    if (utc_stamp === undefined) return undefined;
    stamp = utc_stamp;
  }
  const pattern = FORMATTERS[format || 'default'] || format || FORMATTERS.default;
  if (!stamp || stamp === '') return 'error';
  if (utcz) return new Date(stamp).toISOString();
  const date = parseTimestamp(stamp);
  const offset = date && getZoneOffset(date, time_zone);
  if (!date || offset === undefined) return undefined;
  return formatWallTime(date.getTime(), offset, pattern);
};

const toZonedIsoText = (
  timestamp: string | Date | number, timeZone = 'UTC', format?: string,
): string | undefined => {
  const date = parseTimestamp(timestamp);
  if (!date) return undefined;
  const offset = getZoneOffset(date, timeZone);
  if (offset === undefined) return undefined;
  const shifted = parseTimestamp(date.getTime() + offset * 60000);
  if (!shifted) return undefined;
  const text = shifted.toISOString();
  if (format === 'yyyy-MM-dd hh-mm') return `${text.slice(0, 10)} ${text.slice(11, 16).replace(':', '-')}`;
  if (format === 'yyyy-MM-dd') return text.slice(0, 10);
  if (format !== undefined) return undefined;
  const absolute = Math.abs(offset);
  const tz = offset === 0 ? 'Z' : `${offset < 0 ? '-' : '+'}${PAD(Math.floor(absolute / 60))}:${PAD(absolute % 60)}`;
  return `${text.slice(0, -1)}${tz}`;
};

/** Append the target wall time's offset. Ambiguous/nonexistent times fail. */
const fromWallText = (text: string, timeZone: string): string | undefined => {
  const wall = parseTimestamp(`${text}Z`);
  if (!wall) return undefined;
  const fixed = fixedOffset(timeZone);
  if (fixed !== undefined) {
    const absolute = Math.abs(fixed);
    const tz = fixed === 0 ? 'Z' : `${fixed < 0 ? '-' : '+'}${PAD(Math.floor(absolute / 60))}:${PAD(absolute % 60)}`;
    const result = `${text}${tz}`;
    return parseTimestamp(result) ? result : undefined;
  }
  // Sample both sides of a transition, then round-trip each candidate.
  const offsets = new Set([-36, 0, 36].map(hours => getZoneOffset(wall.getTime() + hours * 3600000, timeZone)));
  const matches: string[] = [];
  for (const offset of offsets) {
    if (offset === undefined) continue;
    const candidate = toZonedIsoText(wall.getTime() - offset * 60000, timeZone);
    if (candidate?.slice(0, 23) === wall.toISOString().slice(0, 23)) matches.push(candidate);
  }
  return matches.length === 1 ? matches[0] : undefined;
};

/** Calendar arithmetic on wall text, followed by target-offset resolution. */
export const shiftCalendarStamp = (
  stamp: string | number | Date, increment: string, amount: number, timeZone = 'UTC',
): string | undefined => {
  const unit = unitName(increment);
  const text = toZonedIsoText(stamp, timeZone);
  if (!unit || !text || !Number.isSafeInteger(amount)) return undefined;
  const wall = parseTimestamp(`${text.slice(0, 23)}Z`);
  if (!wall) return undefined;
  if (unit === 'second') wall.setUTCSeconds(wall.getUTCSeconds() + amount);
  if (unit === 'minute') wall.setUTCMinutes(wall.getUTCMinutes() + amount);
  if (unit === 'hour') wall.setUTCHours(wall.getUTCHours() + amount);
  if (unit === 'day' || unit === 'week') wall.setUTCDate(wall.getUTCDate() + amount * (unit === 'week' ? 7 : 1));
  if (unit === 'month' || unit === 'year') {
    const day = wall.getUTCDate();
    wall.setUTCDate(1);
    wall.setUTCMonth(wall.getUTCMonth() + amount * (unit === 'year' ? 12 : 1));
    const last = new Date(wall.getTime());
    last.setUTCMonth(last.getUTCMonth() + 1, 0);
    wall.setUTCDate(Math.min(day, last.getUTCDate()));
  }
  return Number.isFinite(wall.getTime()) ? fromWallText(wall.toISOString().slice(0, 23), timeZone) : undefined;
};

/** One boundary owner: replace date/time text, append tz, then validate. */
export const getDateBoundary = (
  stamp: string | number | Date, increment: string, timeZone = 'UTC', end = false,
): string | undefined => {
  const unit = unitName(increment);
  const zoned = toZonedIsoText(stamp, timeZone);
  if (!unit || !zoned) return undefined;
  let text = zoned.slice(0, 19);
  if (unit === 'minute') text = `${text.slice(0, 16)}:00`;
  if (unit === 'hour') text = `${text.slice(0, 13)}:00:00`;
  if (unit === 'day' || unit === 'week') text = `${text.slice(0, 10)}T00:00:00`;
  if (unit === 'month') text = `${text.slice(0, 7)}-01T00:00:00`;
  if (unit === 'year') text = `${text.slice(0, 4)}-01-01T00:00:00`;
  if (unit === 'week') {
    const calendar = parseTimestamp(`${text}Z`)!;
    calendar.setUTCDate(calendar.getUTCDate() - (calendar.getUTCDay() + 6) % 7);
    text = calendar.toISOString().slice(0, 19);
  }
  const start = fromWallText(text, timeZone);
  return start === undefined ? undefined : end ? shiftCalendarStamp(start, unit, 1, timeZone) : start;
};

const packZonedTime = (stamp: string | number | Date, timeZone: string) => {
  const date = parseTimestamp(stamp);
  const zoned = date && toZonedIsoText(date, timeZone);
  return date && zoned ? { date, zoned, utc: date.toISOString() } : undefined;
};

export const getCurrentZonedTime = (timeZone = 'UTC', endOfDay?: boolean) => {
  const now = new Date(Math.round(Date.now() / 5000) * 5000);
  const nextDay = endOfDay ? getDateBoundary(now, 'day', timeZone, true) : undefined;
  const end = nextDay === undefined ? undefined : parseTimestamp(nextDay);
  if (endOfDay && !end) return undefined;
  const packed = packZonedTime(end ? end.getTime() - 1000 : now, timeZone);
  return packed ? { ...packed, string: packed.zoned } : undefined;
};

export const generateZonedTimestamp = (
  timeZone = 'UTC',
  { subtractSeconds = 0, subtractDays = 0, subtractHours = 0, subtractMinutes = 0,
    subtractWeeks = 0, subtractMonths = 0, startOf }: GenerateZonedStartTimeProps = {},
) => {
  const amounts = [subtractSeconds, subtractDays, subtractHours, subtractMinutes, subtractWeeks, subtractMonths];
  if (!amounts.every(Number.isSafeInteger)) return undefined;
  const now = getCurrentZonedTime(timeZone);
  if (!now) return undefined;
  const boundary = startOf ?? (subtractWeeks ? 'week' : subtractMonths ? 'month' : subtractDays ? 'day' : undefined);
  let stamp = boundary === undefined ? now.zoned : getDateBoundary(now.date, boundary, timeZone);
  if (stamp === undefined) return undefined;
  // Preserve the inclusive day/month window used by the demo generator.
  const steps: [string, number][] = [
    ['month', subtractMonths ? subtractMonths - 1 : 0], ['week', subtractWeeks],
    ['day', subtractDays ? subtractDays - 1 : 0], ['hour', subtractHours],
    ['minute', subtractMinutes], ['second', subtractSeconds],
  ];
  for (const [unit, amount] of steps) {
    if (amount === 0) continue;
    stamp = shiftCalendarStamp(stamp, unit, -amount, timeZone);
    if (stamp === undefined) return undefined;
  }
  return packZonedTime(stamp, timeZone);
};

export const generateZonedTimeframe = (
  timeZone = 'UTC', props: GenerateZonedStartTimeProps = {}, returnUtc?: boolean,
): { startTime: string; endTime: string } | undefined => {
  const start = generateZonedTimestamp(timeZone, props);
  const end = getCurrentZonedTime(timeZone);
  return start && end ? { startTime: returnUtc ? start.utc : start.zoned, endTime: returnUtc ? end.utc : end.zoned } : undefined;
};
